export type SignupStep = 1 | 2 | 3 | 4;

export const SIGNUP_SCREENSHOT_NAME = {
  firstName: "José",
  lastName: "Ramírez",
} as const;

export const SIGNUP_SCREENSHOT_EMAIL = "jose@example.com";

const MIN_PASSWORD = 8;

export function passwordsMatch(password: string, confirm: string): boolean {
  return password.length > 0 && password === confirm;
}

/** Empty until the confirm field has been typed and disagrees. */
export function passwordMismatchMessage(password: string, confirm: string): string {
  if (confirm.length === 0 || passwordsMatch(password, confirm)) return "";
  return "Passwords do not match";
}

export type SignupContinueInput = {
  step: SignupStep;
  firstName: string;
  lastName: string;
  addressOk: boolean;
  emailProof: string;
  password: string;
  confirm: string;
};

export function canContinueSignup(input: SignupContinueInput): boolean {
  if (input.step === 1) {
    return Boolean(input.firstName.trim() && input.lastName.trim() && input.addressOk);
  }
  if (input.step === 2) return Boolean(input.emailProof.trim());
  if (input.step === 3) {
    return input.password.length >= MIN_PASSWORD && passwordsMatch(input.password, input.confirm);
  }
  return false;
}

export function signupProgress(step: SignupStep): number {
  return step / 4;
}

/** Wait before `handleAvailable` while the username field is being typed. */
export const SIGNUP_HANDLE_DEBOUNCE_MS = 350;

/** Plan card price. Ledger cents are TTD. `en-US` so server and client match. */
export function formatSignupPlanPrice(cents: number): string {
  const amount = Math.max(0, Math.round(cents)) / 100;
  const face = amount.toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `TT$${face}`;
}

/** Inline status under the username field. Empty when there is nothing to say. */
export function signupHandleMessage(input: {
  localPart: string;
  checking: boolean;
  availability: { ok: boolean; reason?: string } | null;
}): string {
  if (!input.localPart) return "";
  if (input.localPart.length < 3) return "At least 3 characters";
  if (input.checking) return "Checking";
  if (!input.availability) return "";
  if (input.availability.ok) return "Available";
  if (input.availability.reason === "taken") return "Taken";
  if (input.availability.reason === "reserved") return "Reserved";
  return "Invalid";
}

export function signupPreviewHost(hostname: string): boolean {
  return hostname === "preview.studio.yatishara.com" || hostname === "localhost";
}

/** Dev screenshots only. Live studio.yatishara.com ignores the param. */
export function signupScreenshotStep(hostname: string, search: string): SignupStep | null {
  if (!signupPreviewHost(hostname)) return null;
  const raw = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get(
    "signupStep",
  );
  if (raw === "1" || raw === "2" || raw === "3" || raw === "4") {
    return Number(raw) as SignupStep;
  }
  return null;
}

const SIGNUP_PLAN_SLUGS = ["core", "plus", "pro"] as const;
export type SignupPlanSlug = (typeof SIGNUP_PLAN_SLUGS)[number];

/** Dev screenshots: `?signupPlan=plus` selects that plan on step 4. */
export function signupScreenshotPlan(hostname: string, search: string): SignupPlanSlug | null {
  if (!signupPreviewHost(hostname)) return null;
  const raw = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search).get(
    "signupPlan",
  );
  if (raw === "core" || raw === "plus" || raw === "pro") return raw;
  return null;
}
